import React, { createContext, useContext, useMemo } from 'react'

import { ExtendedPaletteColorName } from '../../shared/MobiusColors'
import type { SemanticColorToken } from '../themes'
import { CssVars, type ThemeColorToken } from '../themes'
import { Logo } from './Logo'
import { getColorValue, useTheme } from './ThemeProvider'
import { Type } from './Type'

type Props = {
  children: React.ReactNode
  backgroundColor?: SemanticColorToken | ThemeColorToken
  foregroundColor?: SemanticColorToken | ThemeColorToken
  /**
   * We strongly suggest using the `backgroundColor` prop. Please only use this override property if the
   * colors you need are not Bento supported. Due to the special need for customization beyond the
   * Bento theme, this component can take a string for the CSS color values.
   */
  backgroundColorOverride?: string
}

const CelebrationCardContext = createContext<
  | {
      backgroundColor: Props['backgroundColor']
      foregroundColor: Props['foregroundColor']
      backgroundColorOverride: Props['backgroundColorOverride']
      foregroundColorVariable: string
      backgroundColorVariable: string
    }
  | undefined
>(undefined)
function useCelebrationCardContext(componentName: string) {
  const context = useContext(CelebrationCardContext)
  if (!context) {
    throw new Error(
      `Modal: Missing context provider. Are you using ${componentName} outside CelebrationCard?`
    )
  }
  return context
}

/**
 * The `CelebrationCard` is like a special receipt, used to celebrate specific transactions or rewards. Because CelebrationCard
 * is not widely used, it has limited variants. The content is very flexible and you can show/hide the layers. The CelebrationCard
 * should be used sparingly and reserved for those special transactions (i.e. Rewards, ACATS-in) that do not have a frequent recurrence.
 */
export function CelebrationCard({
  backgroundColor = ExtendedPaletteColorName.XRayLight,
  backgroundColorOverride,
  foregroundColor = ExtendedPaletteColorName.Jet,
  children,
}: Props) {
  const theme = useTheme()

  const backgroundColorVariable =
    backgroundColorOverride ?? getColorValue(backgroundColor, theme)
  const foregroundColorVariable = getColorValue(foregroundColor, theme)

  const contextValue = useMemo(
    () => ({
      backgroundColor,
      backgroundColorOverride,
      backgroundColorVariable,
      foregroundColor,
      foregroundColorVariable,
    }),
    [
      backgroundColor,
      backgroundColorOverride,
      backgroundColorVariable,
      foregroundColor,
      foregroundColorVariable,
    ]
  )

  return (
    <CelebrationCardContext.Provider value={contextValue}>
      <section
        css={{
          backgroundColor: backgroundColorVariable,
          borderRadius: 8,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: 324,
          minHeight: 324,
          padding: '8px 24px 16px 24px',
          width: '100%',
        }}
      >
        {children}
      </section>
    </CelebrationCardContext.Provider>
  )
}

type CelebrationCardHeaderProps = { children?: React.ReactNode; logo?: boolean }

CelebrationCard.Header = function CelebrationCardHeader({
  children,
  logo = true,
}: CelebrationCardHeaderProps) {
  const context = useCelebrationCardContext('CelebrationCard.Header')

  let body = null
  if (logo) body = <Logo color={context.foregroundColor} />
  if (children) body = children

  return (
    <header
      css={{
        alignItems: 'center',
        display: 'flex',
        height: 48,
        marginBottom: 12,
        marginLeft: 'auto',
      }}
    >
      {body}
    </header>
  )
}

type CelebrationCardBodyProps = {
  header?: React.ReactNode
  eyebrow?: React.ReactNode
  message?: React.ReactNode
}

CelebrationCard.Body = function CelebrationCardBody({
  eyebrow,
  header,
  message,
}: CelebrationCardBodyProps) {
  const context = useCelebrationCardContext('CelebrationCard.Body')

  return (
    <main css={{ flex: 1, marginTop: CssVars.size.gutterWidthSm }}>
      <div css={{ marginBottom: CssVars.size.gutterWidthSm }}>
        <Type.Text color={context.foregroundColor} isBold size="S">
          {eyebrow}
        </Type.Text>
      </div>
      <div css={{ marginBottom: CssVars.size.gutterWidthSm }}>
        <Type.Display color={context.foregroundColor} size="S">
          {header}
        </Type.Display>
      </div>
      <div css={{ marginBottom: 24 }}>
        <Type.Text color={context.foregroundColor} size="M">
          {message}
        </Type.Text>
      </div>
    </main>
  )
}

type CelebrationCardFooterProps = {
  startTitle?: React.ReactNode
  startSubtitle?: React.ReactNode
  endTitle?: React.ReactNode
  endSubtitle?: React.ReactNode
}

CelebrationCard.Footer = function CelebrationCardFooter({
  endSubtitle,
  endTitle,
  startSubtitle,
  startTitle,
}: CelebrationCardFooterProps) {
  const context = useCelebrationCardContext('CelebrationCard.Footer')

  return (
    <footer
      css={{
        borderTop: `1px solid ${context.foregroundColorVariable}`,
        display: 'flex',
        gap: CssVars.size.gutterWidth,
        paddingTop: 16,
      }}
    >
      <div
        css={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
        }}
      >
        <div>
          <Type.Text
            color={context.foregroundColor}
            isBold
            maxLines={1}
            size="L"
          >
            {startTitle}
          </Type.Text>
        </div>
        <div>
          <Type.Text color={context.foregroundColor} maxLines={1} size="S">
            {startSubtitle}
          </Type.Text>
        </div>
      </div>
      <div css={{ display: 'flex', flexDirection: 'column' }}>
        <Type.Text
          color={context.foregroundColor}
          isBold
          maxLines={1}
          overrideRootStyles={{ textAlign: 'right' }}
          size="L"
        >
          {endTitle}
        </Type.Text>
        <Type.Text
          color={context.foregroundColor}
          maxLines={1}
          overrideRootStyles={{ textAlign: 'right' }}
          size="S"
        >
          {endSubtitle}
        </Type.Text>
      </div>
    </footer>
  )
}
